// ═══════════════════════════════════════════════════
//  ReconAI Pro v3 – PDF Parsing Engine (v6)
//  Tested against: PAVOS TEX company (wibserp subledger),
//  FAYALEEN supplier (Tally classic To/By), NOVELTY TEXTILES
//  company (wibserp creditor-for-expense), PAULCART supplier
//  (wibserp debtor). Enhanced reference extraction for cross-
//  matching invoice refs (FI xxx), bill refs (RBD xx), cheque
//  refs (CH#xxx), DN/CN refs.
// ═══════════════════════════════════════════════════

import { generateId, parseDateToISO, parseAmount, extractVoucherNumber } from '../utils/helpers';

// ── PDF.js Initialization ───────────────
let pdfjsLib = null;
const PDFJS_CDNS = [
  'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js',
  'https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.min.js',
  'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/build/pdf.min.js',
];
const PDFJS_WORKERS = [
  'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js',
  'https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js',
  'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/build/pdf.worker.min.js',
];
const getPdfjs = async () => {
  if (pdfjsLib) return pdfjsLib;
  if (window.pdfjsLib) { pdfjsLib = window.pdfjsLib; return pdfjsLib; }

  // Try multiple CDNs in order
  let loaded = false;
  for (let ci = 0; ci < PDFJS_CDNS.length; ci++) {
    try {
      await new Promise((resolve, reject) => {
        // Check if script already exists
        const existing = document.querySelector(`script[src="${PDFJS_CDNS[ci]}"]`);
        if (existing) { resolve(); return; }
        const script = document.createElement('script');
        script.src = PDFJS_CDNS[ci];
        script.onload = () => {
          if (window.pdfjsLib) {
            window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKERS[ci];
          }
          resolve();
        };
        script.onerror = () => reject(new Error(`CDN ${ci + 1} failed`));
        document.head.appendChild(script);
      });
      if (window.pdfjsLib) { loaded = true; break; }
    } catch (e) {
      console.warn(`PDF.js CDN ${ci + 1} failed, trying next...`, e.message);
    }
  }

  pdfjsLib = window.pdfjsLib;
  if (!pdfjsLib) throw new Error('Could not load PDF.js from any CDN. Check your internet connection and refresh the page.');
  return pdfjsLib;
};

const parseAnyAmount = (s) => {
  if (!s) return 0;
  s = String(s).trim();
  const neg = s.startsWith('(') && s.endsWith(')');
  s = s.replace(/[()₹$€£¥RSINR\s]/gi, '').replace(/,/g, '').replace(/\s*(Dr|Cr|DR|CR)\s*$/i, '');
  const num = parseFloat(s);
  return (isNaN(num) ? 0 : neg ? -Math.abs(num) : num);
};

const DATE_PATTERNS = [
  /\d{2}[-/.]\d{2}[-/.]\d{4}/, /\d{4}[-/.]\d{2}[-/.]\d{2}/,
  /\d{1,2}[-/.]\w{3}[-/.]\d{4}/, /\d{1,2}[-/.]\w{3}[-/.]\d{2}/,
];
const isDateLike = (s) => s ? DATE_PATTERNS.some(p => p.test(String(s).trim())) : false;

// ── Skip Line Detection ─────────────────
const isSkipLine = (text) => {
  const t = text.trim();
  if (!t || t.length < 3) return true;
  if (/^(DATE\s+DOC|OPENING\s+BALANCE|CURRENT\s+TOTAL|CLOSING\s+BALANCE|GRAND\s+TOTAL)/i.test(t)) return true;
  if (/(?:To|By)\s+(?:Opening|Closing)\s+Balance|Brought\s+Forward|Carried\s+(Forward|Over)/i.test(t)) return true;
  if (/^(Cr\s+|Dr\s+)?(Closing|Opening)\s*Balance/i.test(t)) return true;
  if (/^\s*(Carried\s+Over|Brought\s+Forward)\s*$/i.test(t)) return true;
  if (/^(Cr\s+)?Total\b/i.test(t) || /^Grand\s+Total\b/i.test(t)) return true;
  if (/^[\d,.\s]+$/.test(t)) return true;
  if (/^(SUMMARY\s+REPORT|Subledger\s+Rpt|ncpl\.wibserp|controller\d*\.php)/i.test(t)) return true;
  if (/^\[?\d{4}\]\s*(SUNDRY|CREDITORS|DEBTORS)/i.test(t)) return true;
  if (/^[-_=*\s]+$/.test(t)) return true;
  if (/^page\s*\d+$/i.test(t)) return true;
  if (/^CIN:|^E-?Mail\s*:/i.test(t)) return true;
  if (/CREDITORS\s+FOR\s+EXPENSE|EXPENSES\s+PAYABLE/i.test(t) && t.length < 80) return true;
  if (/SUNDRY\s+DEBTORS|SUNDRY\s+CREDITORS/i.test(t) && t.length < 80) return true;
  if (/^\[\d+\]\s*(CREDITORS|SUNDRY)/i.test(t)) return true;
  if (/^\(?\s*A?\s*DIV\s+OF/i.test(t)) return true;
  if (/^Liabilities\s+EXPENSES|^Assets\s+SUNDRY/i.test(t)) return true;
  if (/^continued\s*\.{0,3}$/i.test(t) || /^continued\s/i.test(t)) return true;
  // Narration continuation fragments from wibserp/multi-line entries — NOT transactions
  if (/^Bill\s*Amt\s+[\d,.]+/i.test(t)) return true;
  if (/^Amt\s+[\d,.]+\s*:::/i.test(t)) return true;
  if (/^:::\s*$/i.test(t)) return true;
  if (/^Inv#\d.*Dated\s+on\s+\d/i.test(t) && !/^\d{2}[-/.]\d{2}[-/.]\d{4}/.test(t)) return true;
  if (/^CH#\d+\s+(Inv#|DEBIT\s*NOTE)/i.test(t)) return true;
  if (/^Dated\s+on\s+\d{4}/i.test(t)) return true;
  // Standalone "Amt NNN" without "Bill" prefix — narration continuation
  if (/^\s*Amt\s+[\d,]+\.?\d*\s*$/i.test(t)) return true;
  // "DEBIT NOTE#93-2026-01-14 Inv#..." continuation lines (no leading date)
  if (/^\s*DEBIT\s*NOTE#[\d-]+\s+Inv#/i.test(t) && !/^\d{2}[-/.]\d{2}[-/.]\d{4}/.test(t)) return true;
  // Standalone bill amount fragments: "61,234.00" or "Bill 123860"
  if (/^\s*Bill\s+\d+\s*$/i.test(t)) return true;
  // wibserp footer lines: URLs, timestamps, page numbers
  if (/^\d{1,2}\/\d{1,2}\/\d{2,4},\s*\d{1,2}:\d{2}\s*(AM|PM)/i.test(t)) return true;
  if (/controller\d*\.php|wibserp|\.247:\d{4}\//i.test(t)) return true;
  if (/^Liabilities\s+(SCR|SUNDRY)/i.test(t) || /^\[\d+\]\s*SUNDRY/i.test(t)) return true;
  if (/^(Plot\s+No|Contact\s*:|ERNAKULAM|KERALA|JAIPUR|RAJASTHAN|OPP\.\s*SOUTH|MARKET\s*ROAD)/i.test(t) && t.length < 80) return true;
  if (/PALARIVATTOM|COMPANYPADY|CHOORNIKKARA|FATHIMA\s*MATH|GREEN\s*EARTH/i.test(t) && t.length < 80) return true;
  if (/^41\/1010A|^96,\s*FATHIMA/i.test(t)) return true;
  // Skip all-caps header lines without numbers (company name repetitions)
  if (/^[A-Z][A-Z\s&.,()]+$/.test(t) && t.length < 70 && !/\d/.test(t) && /(PVT|LTD|LIMITED|INDUSTRIES|TEXTILES|COMMERCE|RETAIL)/i.test(t)) return true;
  // Skip narration continuation lines (wibserp multi-line narration fragments)
  // "Bill Amt 45613.00 :::" — narration wrapping from a table cell
  if (/^\s*Bill\s*Amt\s+[\d,.]+\s*:::/i.test(t)) return true;
  // Pure ":::" lines or lines starting with ":::"
  if (/^\s*:::/.test(t)) return true;
  return false;
};

// ── Metadata Extraction ─────────────────
const extractMetadata = (lineTexts) => {
  const meta = { company: '', supplier: '', period: '', gst: '', accountType: '', openingBalance: null, closingBalance: null };
  const hdr = lineTexts.slice(0, Math.min(30, lineTexts.length)).join(' ');
  if (/CREDITORS|PAYABLE/i.test(hdr)) meta.accountType = 'creditor';
  else if (/DEBTORS|RECEIVABLE/i.test(hdr)) meta.accountType = 'debtor';
  const names = [];
  const re = /([A-Z][A-Z\s&.,()]+(?:PVT|PRIVATE|LTD|LIMITED|LLP|TEXTILES|INDUSTRIES|ENTERPRISES|COMMERCE)[\sA-Z.()]*)/g;
  let m; while ((m = re.exec(hdr)) !== null) { const n = m[1].trim().replace(/\s+/g, ' '); if (n.length > 5 && !names.includes(n)) names.push(n); }
  if (names.length >= 2) { meta.company = names[0]; meta.supplier = names[1]; }
  else if (names.length === 1) meta.company = names[0];
  const gm = hdr.match(/GST[:\s]*([0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9A-Z]{3})/i);
  if (gm) meta.gst = gm[1];
  const pm = hdr.match(/(\d{1,2}-\w{3}-\d{2,4})\s+to\s+(\d{1,2}-\w{3}-\d{2,4})/i) || hdr.match(/From\s+(\d{2}-\d{2}-\d{4})\s+To\s+(\d{2}-\d{2}-\d{4})/i);
  if (pm) meta.period = `${pm[1]} to ${pm[2]}`;

  // Extract Opening Balance and Closing Balance amounts
  const allText = lineTexts.join('\n');
  for (const line of lineTexts) {
    const lt = line.trim();
    // Opening Balance patterns: "Opening Balance  1,23,456.78 Cr" or "To Opening Balance 2,71,999.01"
    const obm = lt.match(/(?:To\s+)?Opening\s*Balance[^0-9]*([\d,]+\.?\d*)\s*(Cr|Dr)?/i) ||
                lt.match(/Opening\s*Balance[^0-9]*([\d,]+\.?\d*)/i);
    if (obm && meta.openingBalance === null) {
      let amt = parseAnyAmount(obm[1]);
      // If Cr suffix or if found in By column (credit side), make it negative (credit convention)
      const isCr = (obm[2] && /cr/i.test(obm[2])) || /^By\s+/i.test(lt);
      const isDr = (obm[2] && /dr/i.test(obm[2])) || /^To\s+/i.test(lt);
      if (isCr) amt = -amt; // Credit = negative (payable)
      else if (isDr) amt = amt; // Debit = positive (receivable)
      else if (meta.accountType === 'debtor') amt = amt; // Default to DR for debtor accounts
      else amt = -amt; // Default to credit for creditor accounts
      meta.openingBalance = amt;
    }
    // Closing Balance patterns
    // IMPORTANT: In Tally, the closing balance is placed on the OPPOSITE side to balance the account:
    //   "By Closing Balance" → actual balance is DEBIT (positive/receivable)
    //   "To Closing Balance" → actual balance is CREDIT (negative/payable)
    //   For explicit Cr/Dr suffix (e.g., "Closing Balance 1,72,967.00 Dr"), use the suffix directly.
    //   For wibserp/non-Tally (no To/By), default to CR (creditor payable).
    const cbm = lt.match(/(?:(?:To|By)\s+)?Closing\s*Balance[^0-9]*([\d,]+\.?\d*)\s*(Cr|Dr)?/i) ||
                lt.match(/Closing\s*Balance[^0-9]*([\d,]+\.?\d*)/i);
    if (cbm && meta.closingBalance === null) {
      let amt = parseAnyAmount(cbm[1]);
      const hasByPrefix = /^By\s+/i.test(lt);
      const hasToPrefix = /^To\s+/i.test(lt);
      const hasCrSuffix = cbm[2] && /cr/i.test(cbm[2]);
      const hasDrSuffix = cbm[2] && /dr/i.test(cbm[2]);
      if (hasCrSuffix) amt = -amt;           // Explicit CR suffix → negative (payable)
      else if (hasDrSuffix) amt = amt;       // Explicit DR suffix → positive (receivable)
      else if (hasByPrefix) amt = amt;       // Tally: "By Closing Bal" → actual is DR (positive)
      else if (hasToPrefix) amt = -amt;      // Tally: "To Closing Bal" → actual is CR (negative)
      else if (meta.accountType === 'debtor') amt = amt; // Default: DR for debtor accounts
      else amt = -amt;                        // Default: CR for creditor accounts
      meta.closingBalance = amt;
    }
  }
  return meta;
};

// ── Enhanced Reference Extraction ───────
export const extractReferences = (narr) => {
  const refs = { invoiceRef: '', billRef: '', chequeRef: '', dnCnRef: '', paymentRef: '' };
  if (!narr) return refs;
  // Invoice: FI109/25-26, FI 112/25-26
  let m = narr.match(/(?:Inv#|INV#|INVO\.?NO[:\s]*)\s*(FI\s*[\d/]+-[\d/-]+)/i) || narr.match(/(FI\s*\d+\/\d+-\d+)/i);
  if (m) refs.invoiceRef = m[1].replace(/\s+/g, '').toUpperCase();

  // Invoice: PI (Purchase Invoice) number patterns — PI25/956, PI2517196
  if (!refs.invoiceRef) {
    m = narr.match(/(PI\s*\d+\/[\d-]+)/i) || narr.match(/\b(PI\d{4,})\b/i);
    if (m) refs.invoiceRef = m[1].replace(/\s+/g, '').toUpperCase();
  }

  // Invoice: Inv# followed by nnn/YYYY-YYYY (e.g., Inv#76/2025-2026, Inv#766/2024-2025)
  if (!refs.invoiceRef) {
    m = narr.match(/Inv#\s*(\d+\/\d{4}-\d{2,4})/i);
    if (m) refs.invoiceRef = m[1];
  }
  // Invoice: plain numeric — Inv#2517196, INV#2515612
  if (!refs.invoiceRef) {
    m = narr.match(/Inv(?:oice)?#\s*(\d{4,})/i);
    if (m) refs.invoiceRef = m[1];
  }

  // Bill: RBD25/11, RBD24/72, RBS24/97, EBD25/67
  m = narr.match(/(?:BILL\s*NO\.?\s*#?\s*|INVO\.?NO[:\s]*|Bill\s+no\s+#\s*)((?:RB[DS]?|EBD)\d+\/\d+)/i) || narr.match(/((?:RB[DS]?|EBD)\d+\/\d+)/i);
  if (m) refs.billRef = m[1].toUpperCase();
  // Standalone Bill number: "Bill 123860", "Bill No 123860" (without RBD/EBD prefix)
  if (!refs.billRef) {
    m = narr.match(/Bill\s*(?:No\.?\s*)?#?\s*(\d{4,})/i);
    if (m) refs.billRef = m[1];
  }
  // Cheque: CH#719570, CH#767128
  m = narr.match(/CH#(\d+)/i);
  if (m) refs.chequeRef = 'CH#' + m[1];
  // DN/CN ref: DEBIT NOTE#65, DN/FY/028, ESV25/61, DEBIT NOTE#93-2026-01-14
  m = narr.match(/DEBIT\s*NOTE#([\d][\w-]*)/i);
  if (m) refs.dnCnRef = m[1];
  m = narr.match(/Credit\s*Note\s+(\d+)/i);
  if (m && !refs.dnCnRef) refs.dnCnRef = m[1];
  // SCN ref: SCN/25-26/0601, SCN25/123 etc.
  m = narr.match(/(SCN\s*\/?\s*[\d/-]+)/i);
  if (m && !refs.dnCnRef) refs.dnCnRef = m[1].replace(/\s+/g, '').toUpperCase();
  // Receipt ref
  m = narr.match(/Receipt\s+(\d+)/i);
  if (m) refs.paymentRef = 'R' + m[1];
  // Voucher number pattern: nnn/YYYY-YYYY (e.g., 76/2025-2026)
  if (!refs.invoiceRef) {
    m = narr.match(/(\d+\/\d{4}-\d{4})/);
    if (m) refs.invoiceRef = m[1];
  }
  return refs;
};

// ── Transaction Type Detection ──────────
export const detectTxType = (docNo, narration) => {
  const doc = (docNo || '').toUpperCase(), narr = (narration || '').toUpperCase();
  if (/^(PI|FI)\d/i.test(doc)) return 'Purchase';
  if (/^(SI|INV)\d/i.test(doc)) return 'Sales';
  if (/^(BPN|BPS)\d/i.test(doc)) return 'Payment';
  if (/^(BR)\d/i.test(doc)) return 'Receipt';
  if (/^(DN)\d/i.test(doc)) return 'Debit Note';
  if (/^CN\d/i.test(doc)) return 'Credit Note';
  if (/^JW\d/i.test(doc)) return 'Jobwork';
  if (/^JV\d/i.test(doc)) return 'Journal';
  if (/^(EVS|ESV)\d/i.test(doc)) return 'Expense Voucher';
  if (/^(SCN)\d/i.test(doc)) return 'Sales Return';
  // SYSTEM doc no: identify Sales vs Sales Return vs other from narration
  if (/^System\b/i.test(doc)) {
    if (/\bSALES?\s*RETURN/i.test(narr)) return 'Sales Return';
    if (/\bDEBIT\s*NOTE/i.test(narr)) return 'Debit Note';
    if (/\bCREDIT\s*NOTE/i.test(narr)) return 'Credit Note';
    if (/\b(RECEIPT|RECEIVED)/i.test(narr)) return 'Receipt';
    if (/\b(PAYMENT|PAID|NEFT|RTGS|CHEQUE)/i.test(narr)) return 'Payment';
    if (/\bSALE/i.test(narr) || /Bill\s*no\s*#/i.test(narr)) return 'Sales';
    if (/\bPURCHASE/i.test(narr)) return 'Purchase';
    return 'Sales';
  }
  if (/^PR\d+\/\d/i.test(doc)) return 'Purchase';
  if (/\bSALES?\s*RETURN/i.test(narr)) return 'Sales Return';
  if (/\bPURCHASE/i.test(narr) && !/DEBIT\s*NOTE/i.test(narr)) return 'Purchase';
  if (/\bSALE/i.test(narr)) return 'Sales';
  if (/\b(PAID|PAYMENT|REMIT|CHEQUE|NEFT|RTGS|ADVANCE\s*AMT\s*PAID)\b/i.test(narr)) return 'Payment';
  if (/\b(RECEIPT|RECEIVED|AMOUNT\s*RECEI)/i.test(narr)) return 'Receipt';
  if (/\bDEBIT\s*NOTE\b/i.test(narr)) return 'Debit Note';
  if (/\bCREDIT\s*NOTE\b/i.test(narr)) return 'Credit Note';
  if (/\b(EXCESS|SHORT)\b/i.test(narr)) return 'Excess/Shortage';
  if (/\bTDS\b/i.test(narr)) return 'TDS';
  if (/\bJOBWORK\b/i.test(narr)) return 'Jobwork';
  if (/\b(ROUND\s*OFF|GST\s*AMT|ROC|NON\s*FILING|CONSUMABLE|TRANSFER)\b/i.test(narr)) return 'Journal';
  if (/\b(BILL\s*AMT|SERVICE\s*CHARGE|SOFTWARE|AMC|CLOUD|PAYABLE\s*TO)\b/i.test(narr)) return 'Expense Voucher';
  if (/Bill\s*no\s*#/i.test(narr)) return 'Sales';
  // Bank account narrations — typically Receipt or Payment
  // "HDFC Bank Limited A/C NO. 50200057305265", "SBI A/C No 12345", etc.
  if (/\bBANK\b.*\bA\s*\/?\s*C\b/i.test(narr) || /\bA\s*\/?\s*C\s*NO/i.test(narr)) return 'Receipt';
  return 'Transaction';
};

// ── DR/CR Side Detection ────────────────
const detectSide = (docNo, narration, accountType) => {
  const txType = detectTxType(docNo, narration);
  const doc = (docNo || '').toUpperCase();
  const narr = (narration || '').toUpperCase();
  if (accountType === 'creditor') {
    if (['Purchase', 'Expense Voucher', 'Sales', 'Credit Note'].includes(txType)) return 'CR';
    if (['Payment', 'TDS', 'Debit Note', 'Jobwork'].includes(txType)) return 'DR';
    if (txType === 'Receipt') return 'CR';
    if (txType === 'Sales Return') return 'DR';
    if (txType === 'Excess/Shortage') return /EXCESS/.test(narr) ? 'CR' : 'DR';
    if (txType === 'Journal') {
      if (/REVERSED/i.test(narr)) return 'CR';
      return 'DR';
    }
    if (/^(BPN|BPS)/i.test(doc)) return 'DR';
    // For unknown/Transaction types in creditor accounts, default to CR (purchase/payable is most common)
    return 'CR';
  }
  if (accountType === 'debtor') {
    if (['Sales', 'Expense Voucher', 'Debit Note'].includes(txType)) return 'DR';
    if (['Receipt', 'Payment', 'Credit Note'].includes(txType)) return 'CR';
    if (txType === 'TDS') return 'DR';
    if (txType === 'Purchase') return 'CR';
    if (txType === 'Sales Return') return 'CR';
    if (/^BR/i.test(doc)) return 'CR';
    // For unknown/Transaction types in debtor accounts, default to DR (sales/receivable is most common)
    return 'DR';
  }
  // No account type specified — use transaction type hints
  if (['Purchase', 'Expense Voucher', 'Sales', 'Credit Note'].includes(txType)) return 'CR';
  if (['Payment', 'Receipt', 'TDS', 'Debit Note'].includes(txType)) return 'DR';
  // Truly unknown: look for amount indicators in narration
  if (/\b(PAID|GIVEN|ADVANCE)\b/i.test(narr)) return 'DR';
  if (/\b(RECEIVED|PAYABLE|DUE)\b/i.test(narr)) return 'CR';
  return 'DR';
};

// ── Auto-categorization: map transaction type → categoryId ──
const TYPE_TO_CATEGORY = {
  'Purchase': 'purchase',
  'Sales': 'sales',
  'Payment': 'payment',
  'Receipt': 'receipt',
  'Debit Note': 'debit_note',
  'Credit Note': 'credit_note',
  'Excess/Shortage': 'excess_shortage',
  'Jobwork': 'jobwork',
  'Journal': 'journal',
  'TDS': 'tds',
  'Expense Voucher': 'purchase',   // Expense vouchers map to purchase category
  'Sales Return': 'credit_note',   // Sales returns map to credit note category
  'Transaction': 'other',
};

// Helper to build a transaction object
const makeTx = (date, docNo, narration, debit, credit, meta) => {
  const type = detectTxType(docNo, narration);
  const refs = extractReferences(narration);
  const isoDate = parseDateToISO(date) || date;
  return {
    id: generateId(), date: isoDate, docNo, type, debit, credit,
    narration: (narration || '').substring(0, 500),
    ref: refs.invoiceRef || refs.billRef || docNo,
    invoiceRef: refs.invoiceRef, billRef: refs.billRef,
    chequeRef: refs.chequeRef, dnCnRef: refs.dnCnRef, paymentRef: refs.paymentRef,
    confidence: (isoDate && (debit + credit) > 0) ? 90 : 60,
    matchStatus: 'UNMATCHED',
    categoryId: TYPE_TO_CATEGORY[type] || 'other',
  };
};

const shouldFilterOutTx = (docNo, narration) => {
  const norm = `${docNo || ''} ${narration || ''}`.toLowerCase().replace(/[\s_]+/g, '');
  if (norm.includes('openingbalance') || 
      norm.includes('closingbalance') || 
      norm.includes('broughtforward') || 
      norm.includes('carriedforward') ||
      norm.includes('carriedover') ||
      norm.includes('carrieddown') ||
      norm.includes('broughtdown') ||
      norm.includes('grandtotal') ||
      norm.includes('currenttotal') ||
      norm.endsWith('total') ||
      norm === 'total' ||
      norm === 'crtotal') {
    return true;
  }
  
  const strippedNarr = String(narration || '').trim();
  if (strippedNarr.length > 0 && /^[\d,.\s\-+()₹$€£¥]+$/.test(strippedNarr)) {
    return true;
  }
  // Filter out narration continuation fragments mistakenly captured as transactions
  if (/^\s*Bill\s*Amt\s+[\d,.]+(\.00)?\s*:::/i.test(strippedNarr)) return true;
  if (/^\s*:::/i.test(strippedNarr)) return true;

  return false;
};

// ═════ STRATEGY 1: Subledger Block (wibserp) ═════
const parseSubledgerBlock = (lines) => {
  const lineTexts = lines.map(l => (typeof l === 'string' ? l : l.text) || '');
  const meta = extractMetadata(lineTexts);
  const transactions = [];
  const txPat = /^(\d{2}-\d{2}-\d{4})\s+(\S+)\s+(.*?)\s*([\d,]+\.\d{2})\s*$/;
  const txShort = /^(\d{2}-\d{2}-\d{4})\s+(\S+)\s+([\d,]+\.\d{2})\s*$/;
  const txFlex = /^(\d{1,2}[-/.]\w{3,9}[-/.]\d{2,4})\s+(\S+)\s+(.*?)\s*([\d,]+\.\d{2})\s*$/;
  const txFlexS = /^(\d{1,2}[-/.]\w{3,9}[-/.]\d{2,4})\s+(\S+)\s+([\d,]+\.\d{2})\s*$/;
  const tx2Amt = /^(\d{2}-\d{2}-\d{4})\s+(\S+)\s+(.*?)\s+([\d,]+\.\d{2})\s+([\d,]+\.\d{2})\s*$/;

  // Detect DR/CR column x-positions from header line
  let drColX = -1, crColX = -1;
  for (const lineObj of lines) {
    if (!lineObj.parts) continue;
    const t = (lineObj.text || '').toUpperCase();
    if ((/\bDR\b/.test(t) || /\bDEBIT\b/.test(t)) && (/\bCR\b/.test(t) || /\bCREDIT\b/.test(t))) {
      for (const p of lineObj.parts) {
        const pt = p.text.trim().toUpperCase();
        if (/^(DR|DEBIT)$/.test(pt)) drColX = p.x;
        if (/^(CR|CREDIT)$/.test(pt)) crColX = p.x;
      }
      if (drColX > 0 && crColX > 0) break;
    }
  }
  const hasPositionalCols = drColX > 0 && crColX > 0;

  const txIdxs = [], txD = {};
  for (let i = 0; i < lineTexts.length; i++) {
    const l = lineTexts[i].trim();
    if (isSkipLine(l)) continue;
    let dt, dc, inl = '', as, dr = null, cr = null, m;

    // ── Try positional column parsing first (most accurate for wibserp) ──
    // Use x-coordinates to assign amounts to DR/CR columns
    let positionalParsed = false;
    if (hasPositionalCols && lines[i] && lines[i].parts) {
      const dm = l.match(/^(\d{2}-\d{2}-\d{4})/);
      if (dm) {
        // Find doc no (second token after date)
        const afterDate = l.substring(dm[0].length).trim();
        const docMatch = afterDate.match(/^(\S+)/);
        if (docMatch) {
          let pDr = 0, pCr = 0;
          for (const p of lines[i].parts) {
            const pt = p.text.trim();
            // Only consider formatted amounts (must have .XX decimal)
            if (!/\.\d{2}$/.test(pt)) continue;
            const val = parseAnyAmount(pt);
            if (val <= 0) continue;
            const distToDr = Math.abs(p.x - drColX);
            const distToCr = Math.abs(p.x - crColX);
            if (distToDr < distToCr && distToDr < 100) pDr = val;
            else if (distToCr < 100) pCr = val;
          }
          if (pDr > 0 || pCr > 0) {
            dt = dm[1]; dc = docMatch[1];
            inl = afterDate.substring(docMatch[0].length).trim();
            // Remove amounts from narration text
            inl = inl.replace(/[\d,]+\.\d{2}/g, '').replace(/\s{2,}/g, ' ').trim();
            dr = pDr; cr = pCr;
            positionalParsed = true;
            m = true; // flag that we matched
          }
        }
      }
    }

    // ── Fallback: regex-based parsing ──
    if (!positionalParsed) {
      m = tx2Amt.exec(l);
      if (m) {
        const a1 = parseAnyAmount(m[4]), a2 = parseAnyAmount(m[5]);
        const narrText = m[3].trim();
        // Check if first amount is a narration echo ("Bill Amt NNN" or "Amt NNN")
        const hasNarrAmt = /\b(?:Bill\s*)?Amt\s+[\d,]+/i.test(narrText) || /:::/i.test(narrText);
        if (hasNarrAmt || a1 === a2) {
          // First amount is narration text, not a column amount
          // Use only the LAST amount with detectSide
          m = null; // fall through to single-amount patterns
        } else if (a1 === 0 || (a1 > 0 && a2 > 0 && narrText.length > 0)) {
          dt = m[1]; dc = m[2]; inl = narrText; dr = a1; cr = a2;
        } else {
          m = null;
        }
      }
      if (!m) { m = txPat.exec(l); if (m) { [, dt, dc, inl, as] = m; } else { m = txShort.exec(l); if (m) { [, dt, dc, as] = m; } else { m = txFlex.exec(l); if (m) { [, dt, dc, inl, as] = m; } else { m = txFlexS.exec(l); if (m) { [, dt, dc, as] = m; } } } } }
    }

    if (!m) continue;
    const amt = dr !== null ? Math.max(dr, cr) : parseAnyAmount(as);
    if (amt <= 0 && dr === null) continue; if (dr !== null && dr === 0 && cr === 0) continue;
    // Strip ":::" and narration amounts from inline narration
    if (inl) inl = inl.replace(/\s*:::.*$/, '').replace(/\bBill\s*Amt\s*[\d,.]+/gi, '').trim();
    txIdxs.push(i); txD[i] = { date: dt, doc: dc, inlineNarr: (inl || '').trim(), amount: amt, drAmt: dr, crAmt: cr };
  }

  for (let t = 0; t < txIdxs.length; t++) {
    const idx = txIdxs[t], { date, doc, inlineNarr, amount } = txD[idx];
    const prev = t > 0 ? txIdxs[t - 1] : -1, above = [];
    for (let j = prev + 1; j < idx; j++) { const a = lineTexts[j].trim(); if (!isSkipLine(a) && !/^\d{2}-\d{2}-\d{4}/.test(a)) above.push(a); }
    const next = t < txIdxs.length - 1 ? txIdxs[t + 1] : lineTexts.length, below = [];
    for (let j = idx + 1; j < next; j++) { const b = lineTexts[j].trim(); if (isSkipLine(b) || /^\d{2}-\d{2}-\d{4}/.test(b)) break; below.push(b); }
    const fullNarr = [...above, inlineNarr, ...below].filter(Boolean).join(' ').replace(/\s+/g, ' ').trim();
    const has2 = txD[idx].drAmt !== null;
    let db, cr2;
    if (has2) { db = Math.round(txD[idx].drAmt * 100) / 100; cr2 = Math.round(txD[idx].crAmt * 100) / 100; }
    else { const side = detectSide(doc, fullNarr, meta.accountType); db = side === 'DR' ? Math.round(amount * 100) / 100 : 0; cr2 = side === 'CR' ? Math.round(amount * 100) / 100 : 0; }
    if (!shouldFilterOutTx(doc, fullNarr)) {
      transactions.push(makeTx(date, doc, fullNarr, db, cr2, meta));
    }
  }
  return { transactions, metadata: meta };
};

// ═════ STRATEGY 2: Tally Classic "To/By" ═════
const parseTallyClassic = (lines) => {
  const lineTexts = lines.map(l => (typeof l === 'string' ? l : l.text) || '');
  const meta = extractMetadata(lineTexts);
  const transactions = [];
  let curDate = '';

  // Detect reversed column order: "Credit Debit" instead of "Debit Credit"
  const headerText = lineTexts.join(' ').toUpperCase();
  const creditIdx = headerText.indexOf('CREDIT');
  const debitIdx = headerText.indexOf('DEBIT');
  const reversedCols = creditIdx >= 0 && debitIdx >= 0 && creditIdx < debitIdx;

  for (let i = 0; i < lineTexts.length; i++) {
    const line = lineTexts[i].trim();
    if (!line || isSkipLine(line)) continue;
    if (/Opening\s*Balance|Closing\s*Balance/i.test(line)) continue;
    const dm = line.match(/^(\d{1,2}-[A-Za-z]{3}-\d{2,4})/);
    if (dm) curDate = dm[1];
    let rest = dm ? line.substring(dm[0].length).trim() : line;
    // Match "To " or "By " with space, OR "To"/"By" directly attached to next word (e.g., "ToOpening", "ByHDFC")
    const tbm = rest.match(/^(To|By)\s+(.+)/i) || rest.match(/^(To|By)([A-Z].+)/i);
    if (!tbm) continue;
    const toBy = tbm[1].toLowerCase(), rr = tbm[2].trim();
    const am = rr.match(/([\d,]+\.\d{2})\s*$/);
    if (!am) continue;
    const amount = parseAnyAmount(am[1]);
    if (amount <= 0 || !curDate) continue;
    let mid = rr.replace(/([\d,]+\.\d{2})\s*$/, '').trim();

    // Extract voucher type from "Particulars" column
    let vType = '', vNo = '';
    const vtKw = [{ k: 'SALES TAX INVOICE', t: 'Sales' }, { k: 'SALE', t: 'Sales' }, { k: 'PURCHASE', t: 'Purchase' }, { k: 'RECEIPT', t: 'Receipt' }, { k: 'PAYMENT', t: 'Payment' }, { k: 'CREDIT NOTE', t: 'Credit Note' }, { k: 'DEBIT NOTE', t: 'Debit Note' }, { k: 'JOURNAL', t: 'Journal' }];
    for (const v of vtKw) { if (mid.toUpperCase().includes(v.k)) { vType = v.t; break; } }

    // Extract voucher number from the end of the line
    // Patterns: "Purchase PR25/956", "Sales FI 111/25-26", "Receipt 91", "Credit Note 65", "Debit Note ESV25/61", "Debit Note DN/FY/028"
    const vnPats = [
      /(?:Purchase|Sales)\s+((?:FI|PR|PI|SI)\s*[\d/]+-?[\d/-]*)\s*$/i,
      /(?:Receipt|Payment)\s+(\d+)\s*$/i,
      /(?:Credit\s*Note|Debit\s*Note)\s+((?:ESV|DN|CN|PR)\d*\/?[\w/-]*|\d+)\s*$/i,
      /((?:FI|PR|PI|ESV|DN|CN)\s*[\d/]+-?[\d/-]+)\s*$/i,
      /(\d+\/\d{4}-\d{4})\s*$/,
      /([A-Z]+\d+\/\d+[-\d]*)\s*$/i,
    ];
    for (const vp of vnPats) { const vm = mid.match(vp); if (vm) { vNo = vm[1].trim(); break; } }

    // Special: for FI refs, normalize spaces
    const fiM = mid.match(/(FI\s*\d+\/[\d-]+)/i);
    if (fiM && !vNo) vNo = fiM[1].replace(/\s+/g, '');

    // For Receipt type, number after "Receipt"
    if (vType === 'Receipt' && !vNo) { const rm = mid.match(/Receipt\s+(\d+)/i); if (rm) vNo = rm[1]; }
    if (vType === 'Purchase' && !vNo) { const pm = mid.match(/(PR\d+\/\d+)/i); if (pm) vNo = pm[1]; }

    const isDebit = toBy === 'to';
    const db = isDebit ? Math.round(amount * 100) / 100 : 0;
    const cr = !isDebit ? Math.round(amount * 100) / 100 : 0;

    if (!shouldFilterOutTx(vNo || '', mid)) {
      const tx = makeTx(curDate, vNo || '', mid, db, cr, meta);
      tx.type = vType || tx.type;
      // Override invoiceRef if we found FI ref
      if (fiM) tx.invoiceRef = fiM[1].replace(/\s+/g, '').toUpperCase();
      if (!tx.invoiceRef && vNo && /^FI/i.test(vNo)) tx.invoiceRef = vNo.replace(/\s+/g, '').toUpperCase();
      tx.confidence = 85;
      transactions.push(tx);
    }
  }
  return { transactions, metadata: meta };
};

// ═════ STRATEGY 3: Structured Table ═════
const parseStructuredTable = (lines) => {
  const lineTexts = lines.map(l => (typeof l === 'string' ? l : l.text) || '');
  const meta = extractMetadata(lineTexts);
  const transactions = [];
  let curDate = '';
  lineTexts.forEach(line => {
    line = line.trim(); if (!line || isSkipLine(line)) return;
    // Skip narration continuation lines — these have amounts but are NOT transactions
    // Patterns: "Bill Amt 45613.00 :::", "Inv#766/2024-2025 Dated on ...", pure amounts like "45613.00"
    if (/^\s*Bill\s*Amt\b/i.test(line)) return;
    if (/^\s*:::/.test(line)) return;
    if (/^\s*Inv#/i.test(line) && /Dated\s+on/i.test(line)) return;
    if (/^\s*CH#\d+\s+Inv#/i.test(line)) return;
    if (/^\s*Dated\s+on\s+\d/i.test(line)) return;
    const dm = line.match(/^(\d{1,2}[-/.]\w{3,9}[-/.]\d{2,4}|\d{1,2}[-/.]\d{1,2}[-/.]\d{2,4}|\d{4}[-/.]\d{2}[-/.]\d{2})/);
    if (dm) curDate = dm[1];
    const amts = []; const ar = /(?:^|[\s(₹$])([\d,]+\.\d{2})(?!\S)/g; let a;
    while ((a = ar.exec(line)) !== null) { const v = parseAnyAmount(a[1]); if (v > 0) amts.push({ val: v, pos: a.index }); }
    if (!amts.length || !curDate) return;
    // Detect Dr/Cr indicator — but check if it's a Tally Cr/Dr PREFIX (contra account indicator)
    // vs a Dr/Cr SUFFIX or inline label. Tally prefix format: line starts with [date] Cr/Dr ...
    const tallyPrefix = line.match(/^(?:\d{1,2}[-/.]\w{3,9}[-/.]\d{2,4}\s+)?(Cr|Dr)\s+/i);
    const hasDr = !tallyPrefix && /\bDr\b/.test(line);
    const hasCr = !tallyPrefix && /\bCr\b/.test(line);
    // If Tally prefix detected: "Cr" = party debited = Debit, "Dr" = party credited = Credit
    const tallyDr = tallyPrefix && /^cr$/i.test(tallyPrefix[1]);
    const tallyCr = tallyPrefix && /^dr$/i.test(tallyPrefix[1]);
    let vn = ''; const vm = line.match(/\b([A-Z]{1,6}\d*[\s-/]+\d+(?:[-/]\d+)*)\b/) || line.match(/\b([A-Z]{2,}\d{3,})\b/) || line.match(/\b(System\/\d+)\b/i);
    if (vm && !isDateLike(vm[1])) vn = vm[1].trim();
    let db = 0, cr = 0;
    if (amts.length >= 3) { db = amts[0].val; cr = amts[1].val; }
    else { const amt = amts[0].val; if (hasDr || tallyDr) db = amt; else if (hasCr || tallyCr) cr = amt; else { const s = detectSide(vn, line, meta.accountType); if (s === 'DR') db = amt; else cr = amt; } }
    if (db === 0 && cr === 0) return;
    if (!shouldFilterOutTx(vn || '', line)) {
      const tx = makeTx(curDate, vn, line, Math.round(db * 100) / 100, Math.round(cr * 100) / 100, meta);
      tx.confidence = 75;
      transactions.push(tx);
    }
  });
  return { transactions, metadata: meta };
};

// ═════ STRATEGY 4: Generic Universal ═════
const parseGenericLedger = (lines) => {
  const lineTexts = lines.map(l => (typeof l === 'string' ? l : l.text) || '');
  const meta = extractMetadata(lineTexts);
  const transactions = [];
  let curDate = '';
  lineTexts.forEach(line => {
    line = line.trim(); if (!line || isSkipLine(line)) return;
    // Skip narration continuation lines from wibserp/structured formats
    if (/^\s*Bill\s*Amt\b/i.test(line)) return;
    if (/^\s*:::/.test(line)) return;
    if (/^\s*Inv#/i.test(line) && /Dated\s+on/i.test(line)) return;
    if (/^\s*CH#\d+\s+Inv#/i.test(line)) return;
    if (/^\s*Dated\s+on\s+\d/i.test(line)) return;
    let ds = ''; for (const dp of DATE_PATTERNS) { const dm = line.match(dp); if (dm) { ds = dm[0]; break; } }
    if (ds) curDate = ds; if (!curDate) return;
    const amts = []; const ar = /(?:^|[\s(₹$])([\d,]+\.\d{2})(?!\S)/g; let a;
    while ((a = ar.exec(line)) !== null) { const v = parseAnyAmount(a[1]); if (v > 0.009) amts.push({ val: v, pos: a.index }); }
    if (!amts.length) return;
    let dn = ''; const dm2 = line.match(/\b([A-Z]{1,6}\d*[\s-/]+\d+(?:[-/]\d+)*)\b/) || line.match(/\b([A-Z]{2,}\d{3,})\b/) || line.match(/\b(System\/\d+)\b/i);
    if (dm2 && !isDateLike(dm2[1])) dn = dm2[1].trim();
    let db = 0, cr = 0;
    if (amts.length >= 3) { db = amts[0].val; cr = amts[1].val; }
    else { const amt = amts[0].val; const s = detectSide(dn, line, meta.accountType); if (s === 'DR') db = amt; else cr = amt; }
    if (db === 0 && cr === 0) return;
    if (!shouldFilterOutTx(dn || '', line)) {
      const tx = makeTx(curDate, dn, line, Math.round(db * 100) / 100, Math.round(cr * 100) / 100, meta);
      tx.confidence = 55;
      transactions.push(tx);
    }
  });
  return { transactions, metadata: meta };
};

// ═════ STRATEGY 5: Tally Party Ledger – Positional Column Parser ═════
// Handles Tally PDFs with columns: Date | Particulars | Vch Type | Vch No. | Debit | Credit
// Uses PDF x-coordinates to accurately map each text fragment to its column.
// Supports both To/By and Cr/Dr prefix formats.
const parseTallyPartyLedger = (lines) => {
  const lineTexts = lines.map(l => (typeof l === 'string' ? l : l.text) || '');
  const meta = extractMetadata(lineTexts);
  const transactions = [];
  let curDate = '';

  // ── Step 1: Find the header line with column positions ──
  // Look for lines with "Debit" AND "Credit" text items to establish column x-positions
  let dateColX = -1, particularsColX = -1, vchTypeColX = -1, vchNoColX = -1, debitColX = -1, creditColX = -1;
  let headerFound = false;

  for (const lineObj of lines) {
    if (!lineObj.parts) continue;
    const lt = lineObj.text || '';
    // The header must contain both "Debit" and "Credit"
    if (!/Debit/i.test(lt) || !/Credit/i.test(lt)) continue;
    // Also should have "Date" or "Particulars" or "Vch"
    if (!/Date|Particulars|Vch/i.test(lt)) continue;

    for (const p of lineObj.parts) {
      const t = p.text.trim().toLowerCase();
      if (t === 'date') dateColX = p.x;
      else if (t === 'particulars') particularsColX = p.x;
      else if (/^vch\s*type/i.test(p.text.trim()) || t === 'vch type') vchTypeColX = p.x;
      else if (/^vch\s*no/i.test(p.text.trim()) || t === 'vch no.' || t === 'vch no') vchNoColX = p.x;
      else if (t === 'debit' || t === 'dr') debitColX = p.x;
      else if (t === 'credit' || t === 'cr') creditColX = p.x;
    }
    if (debitColX > 0 && creditColX > 0) { headerFound = true; break; }
  }

  if (!headerFound) {
    // Fallback: find just Debit/Credit
    for (const lineObj of lines) {
      if (!lineObj.parts) continue;
      if (!/Debit/i.test(lineObj.text) || !/Credit/i.test(lineObj.text)) continue;
      for (const p of lineObj.parts) {
        if (/^Debit$/i.test(p.text.trim())) debitColX = p.x;
        if (/^Credit$/i.test(p.text.trim())) creditColX = p.x;
      }
      if (debitColX > 0 && creditColX > 0) { headerFound = true; break; }
    }
  }

  // Also check for Vch Type/No from the header text
  const headerLineText = lineTexts.find(l => /Vch\s*Type/i.test(l) && /Vch\s*No/i.test(l));
  // Don't bail out if we found Debit/Credit columns — we can still parse even without Vch Type/No columns
  if (!headerLineText && !headerFound) return { transactions: [], metadata: meta };

  // Detect which prefix format: To/By or Cr/Dr
  const toByCount = lineTexts.filter(l => /^\s*(\d{1,2}-[A-Za-z]{3}-\d{2,4}\s+)?(To|By)\s+/i.test(l.trim())).length;
  const crDrCount = lineTexts.filter(l => /^\s*(\d{1,2}-[A-Za-z]{3}-\d{2,4}\s+)?(Cr|Dr)\s+/i.test(l.trim())).length;
  const useCrDr = crDrCount > toByCount;

  // ── Helper: classify a text part into a column based on x-coordinate ──
  const classifyColumn = (x) => {
    if (!headerFound) return 'unknown';
    // Build column boundaries from detected header positions
    const cols = [];
    if (dateColX > 0) cols.push({ name: 'date', x: dateColX });
    if (particularsColX > 0) cols.push({ name: 'particulars', x: particularsColX });
    if (vchTypeColX > 0) cols.push({ name: 'vchType', x: vchTypeColX });
    if (vchNoColX > 0) cols.push({ name: 'vchNo', x: vchNoColX });
    if (debitColX > 0) cols.push({ name: 'debit', x: debitColX });
    if (creditColX > 0) cols.push({ name: 'credit', x: creditColX });
    cols.sort((a, b) => a.x - b.x);

    // Find nearest column header
    let nearest = 'unknown', minDist = Infinity;
    for (const c of cols) {
      const d = Math.abs(x - c.x);
      if (d < minDist) { minDist = d; nearest = c.name; }
    }
    // For debit/credit, allow wider tolerance since amounts can be right-aligned
    if (nearest === 'debit' || nearest === 'credit') {
      const dDebit = Math.abs(x - debitColX);
      const dCredit = Math.abs(x - creditColX);
      return dDebit < dCredit ? 'debit' : 'credit';
    }
    return nearest;
  };

  // ── Step 2: Process each line using positional parsing ──
  for (let i = 0; i < lineTexts.length; i++) {
    const line = lineTexts[i].trim();
    if (!line || isSkipLine(line)) continue;
    if (/Opening\s*Balance|Closing\s*Balance|Brought\s*Forward|Carried\s*(Over|Forward|Down)/i.test(line)) continue;

    // Extract date from start of line
    const dm = line.match(/^(\d{1,2}-[A-Za-z]{3}-\d{2,4})/);
    if (dm) curDate = dm[1];

    // Must have To/By or Cr/Dr prefix
    let rest = dm ? line.substring(dm[0].length).trim() : line;
    const tbm = rest.match(/^(To|By|Cr|Dr)\s+(.+)/i);
    if (!tbm) continue;

    const prefix = tbm[1].toLowerCase();
    let rr = tbm[2].trim();

    // Must have at least one formatted amount (with decimal) somewhere in the line
    if (!/([\d,]+\.\d{2})/.test(rr) && !curDate) continue;

    // ── Positional column extraction using PDF parts ──
    let db = 0, cr = 0, vNo = '', vType = '', narration = '';

    if (headerFound && lines[i] && lines[i].parts && lines[i].parts.length > 0) {
      // === POSITIONAL PARSING: use x-coordinates to assign each part to its column ===
      const particulars = [], vchTypeParts = [], vchNoParts = [];

      for (const p of lines[i].parts) {
        const t = p.text.trim();
        if (!t) continue;

        // Skip date portion
        if (dateColX > 0 && Math.abs(p.x - dateColX) < 30 && /^\d{1,2}-[A-Za-z]{3}/.test(t)) continue;
        // Skip Cr/Dr/To/By prefix — it's part of the date column area
        if (/^(Cr|Dr|To|By)$/i.test(t) && p.x < (particularsColX > 0 ? particularsColX : debitColX)) continue;

        const col = classifyColumn(p.x);

        if (col === 'debit') {
          // Only treat as amount if it looks like a formatted number (has decimal)
          if (/\.\d{2}/.test(t)) {
            const v = parseAnyAmount(t);
            if (v > 0) db += v;
          }
        } else if (col === 'credit') {
          if (/\.\d{2}/.test(t)) {
            const v = parseAnyAmount(t);
            if (v > 0) cr += v;
          }
        } else if (col === 'vchNo') {
          vchNoParts.push(t);
        } else if (col === 'vchType') {
          vchTypeParts.push(t);
        } else if (col === 'particulars' || col === 'date') {
          // Particulars — collect narration text (skip Cr/Dr prefix)
          if (!/^(Cr|Dr|To|By)$/i.test(t)) particulars.push(t);
        } else {
          // Unknown column — could be part of particulars or vch type
          particulars.push(t);
        }
      }

      narration = particulars.join(' ').trim();
      const vchTypeStr = vchTypeParts.join(' ').trim();
      const vchNoStr = vchNoParts.join(' ').trim().replace(/\.$/, ''); // remove trailing dot

      // Detect Vch Type from the dedicated column
      if (vchTypeStr) {
        const vtKw = [
          { k: 'SALES CREDIT NOTE', t: 'Sales Return' },
          { k: 'SALES TAX INVOICE', t: 'Sales' }, { k: 'TAX INVOICE', t: 'Sales' },
          { k: 'SALES NEW', t: 'Sales' }, { k: 'SALES RETURN', t: 'Sales Return' },
          { k: 'SALE', t: 'Sales' }, { k: 'PURCHASE', t: 'Purchase' },
          { k: 'RECEIPT', t: 'Receipt' }, { k: 'PAYMENT', t: 'Payment' },
          { k: 'CREDIT NOTE', t: 'Credit Note' }, { k: 'DEBIT NOTE', t: 'Debit Note' },
          { k: 'JOURNAL', t: 'Journal' },
        ];
        for (const v of vtKw) { if (vchTypeStr.toUpperCase().includes(v.k)) { vType = v.t; break; } }
      }

      // Use positional Vch No
      if (vchNoStr) {
        // Clean: extract meaningful vch no patterns
        const vnM = vchNoStr.match(/(SCN\/[\d/-]+)/i) ||
                     vchNoStr.match(/((?:FI|PR|PI|SI|ESV|DN|CN)\s*[\d/]+-?[\d/-]+)/i) ||
                     vchNoStr.match(/(\d+\/\d{4}-\d{4})/) ||
                     vchNoStr.match(/([A-Z]+\d+\/[\d-]+)/i) ||
                     vchNoStr.match(/^(\d{1,10})$/);
        if (vnM) vNo = vnM[1].trim();
        else vNo = vchNoStr; // use as-is
      }
    }

    // ── Fallback: text-based parsing when positional data unavailable ──
    if (db === 0 && cr === 0) {
      // Extract amounts from the text
      const amts = [];
      const amtPat = /([\d,]+\.\d{2})/g;
      let am;
      while ((am = amtPat.exec(rr)) !== null) {
        amts.push({ val: parseAnyAmount(am[1]), pos: am.index });
      }
      if (amts.length === 0) continue;

      if (amts.length >= 2) {
        db = amts[amts.length - 2].val;
        cr = amts[amts.length - 1].val;
      } else {
        const amount = amts[0].val;
        if (amount <= 0) continue;
        if (useCrDr) {
          const isDebit = (prefix === 'cr');
          db = isDebit ? Math.round(amount * 100) / 100 : 0;
          cr = !isDebit ? Math.round(amount * 100) / 100 : 0;
        } else {
          const isDebit = (prefix === 'to');
          db = isDebit ? Math.round(amount * 100) / 100 : 0;
          cr = !isDebit ? Math.round(amount * 100) / 100 : 0;
        }
      }

      // Text-based narration + vch extraction if not already found
      if (!narration) {
        narration = amts.length > 0 ? rr.substring(0, amts[0].pos).trim() : rr;
      }
    }

    // ── Fallback vType detection from narration if column didn't provide it ──
    if (!vType && narration) {
      const vtKw = [
        { k: 'SALES CREDIT NOTE', t: 'Sales Return' },
        { k: 'SALES TAX INVOICE', t: 'Sales' }, { k: 'TAX INVOICE', t: 'Sales' },
        { k: 'SALES NEW', t: 'Sales' }, { k: 'SALES RETURN', t: 'Sales Return' },
        { k: 'SALE', t: 'Sales' }, { k: 'PURCHASE', t: 'Purchase' },
        { k: 'RECEIPT', t: 'Receipt' }, { k: 'PAYMENT', t: 'Payment' },
        { k: 'CREDIT NOTE', t: 'Credit Note' }, { k: 'DEBIT NOTE', t: 'Debit Note' },
        { k: 'JOURNAL', t: 'Journal' },
      ];
      for (const v of vtKw) { if (narration.toUpperCase().includes(v.k) || rr.toUpperCase().includes(v.k)) { vType = v.t; break; } }
    }

    // Detect Receipt/Payment from bank account narrations
    if (!vType && /\bBANK\b.*A\/C|HDFC\s+BANK|SBI\s+BANK|ICICI\s+BANK|AXIS\s+BANK/i.test(narration || rr)) {
      vType = useCrDr ? (prefix === 'dr' ? 'Receipt' : 'Payment') : (prefix === 'by' ? 'Receipt' : 'Payment');
    }

    // Fallback vNo extraction from text if positional didn't find it
    if (!vNo) {
      const vnPats = [
        /(\d+\/\d{4}-\d{4})/, /(SCN\/[\d/-]+)/i,
        /((?:FI|PR|PI|SI|ESV|DN|CN)\s*[\d/]+-?[\d/-]+)/i,
        /([A-Z]+\d+\/\d+[-\d]*)/i,
      ];
      for (const vp of vnPats) { const vm = rr.match(vp); if (vm) { vNo = vm[1].trim(); break; } }
      // Standalone number before amounts
      if (!vNo) {
        const amts = []; const ap = /([\d,]+\.\d{2})/g; let m;
        while ((m = ap.exec(rr)) !== null) amts.push({ pos: m.index });
        if (amts.length > 0) {
          const before = rr.substring(0, amts[0].pos).trim();
          const sn = before.match(/(?:^|\s)(\d{2,8})\s*$/);
          if (sn) vNo = sn[1];
        }
      }
      // Receipt/Payment number after keyword (NOT matching amount starts)
      if (!vNo && vType === 'Receipt') { const rm = rr.match(/Receipt\s+(\d+)(?![,.\d])/i); if (rm) vNo = rm[1]; }
      if (!vNo && vType === 'Payment') { const pm = rr.match(/Payment\s+(\d+)(?![,.\d])/i); if (pm) vNo = pm[1]; }
    }

    if (db === 0 && cr === 0) continue;
    if (!curDate) continue;

    // Use full line text as narration if positional narration is too short
    if (!narration || narration.length < 3) narration = rr;

    if (!shouldFilterOutTx(vNo || '', narration)) {
      const tx = makeTx(curDate, vNo || '', narration, Math.round(db * 100) / 100, Math.round(cr * 100) / 100, meta);
      tx.type = vType || tx.type;
      const voucherNum = extractVoucherNumber(vNo);
      if (voucherNum && !tx.invoiceRef) tx.invoiceRef = vNo;
      tx.confidence = 92;
      transactions.push(tx);
    }
  }
  return { transactions, metadata: meta };
};

// ═════ OCR Fallback: Render PDF pages to canvas + Tesseract.js ═════
let tesseractLoaded = false;
const loadTesseract = async () => {
  if (tesseractLoaded && window.Tesseract) return window.Tesseract;

  // Try loading existing script first
  if (document.querySelector('script[src*="tesseract"]') && window.Tesseract) {
    tesseractLoaded = true;
    return window.Tesseract;
  }

  const cdns = [
    'https://cdn.jsdelivr.net/npm/tesseract.js@5/dist/tesseract.min.js',
    'https://unpkg.com/tesseract.js@5/dist/tesseract.min.js',
    'https://cdn.jsdelivr.net/npm/tesseract.js@4/dist/tesseract.min.js',
  ];

  for (const cdn of cdns) {
    try {
      await new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = cdn;
        s.onload = resolve;
        s.onerror = () => reject(new Error('CDN load failed'));
        document.head.appendChild(s);
      });
      if (window.Tesseract) { tesseractLoaded = true; console.log('[OCR] Tesseract.js loaded from', cdn); return window.Tesseract; }
    } catch (e) { console.warn('[OCR] Failed CDN:', cdn); }
  }
  throw new Error('Could not load Tesseract.js OCR library from any CDN. Check internet connection.');
};

const ocrPdfPages = async (pdfjs, arrayBuffer, numPages) => {
  console.log(`[OCR] Starting OCR for ${numPages} pages...`);
  const Tesseract = await loadTesseract();
  if (!Tesseract) throw new Error('Tesseract.js not available');

  // Create worker — handle both v4 and v5 API
  let worker;
  try {
    // v5 API
    worker = await Tesseract.createWorker('eng', 1, {
      logger: m => { if (m.status) console.log(`[OCR] ${m.status}: ${Math.round((m.progress || 0) * 100)}%`); }
    });
  } catch (e1) {
    try {
      // v4 API fallback
      worker = await Tesseract.createWorker({ logger: m => console.log(`[OCR] ${m.status}`) });
      await worker.loadLanguage('eng');
      await worker.initialize('eng');
    } catch (e2) {
      throw new Error(`Failed to initialize OCR worker: ${e2.message}`);
    }
  }

  // Set optimal OCR parameters for ledger/accounting documents
  try {
    await worker.setParameters({
      tessedit_pageseg_mode: '6',   // Assume uniform block of text (best for tables)
      tessedit_char_whitelist: '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz.,/-#:()[] |\'"&@%+',
      preserve_interword_spaces: '1', // Keep column spacing
    });
  } catch (e) {
    console.warn('[OCR] Could not set parameters:', e.message);
  }

  // Use a fresh copy — getDocument transfers/detaches the ArrayBuffer
  const abCopy = arrayBuffer.slice(0);
  const pdf = await pdfjs.getDocument({ data: abCopy }).promise;
  const allLines = [];
  const SCALE = 3.0; // Higher scale = better OCR accuracy for small text

  for (let pageNum = 1; pageNum <= numPages; pageNum++) {
    try {
      console.log(`[OCR] Rendering page ${pageNum}/${numPages}...`);
      const page = await pdf.getPage(pageNum);
      const viewport = page.getViewport({ scale: SCALE });

      const canvas = document.createElement('canvas');
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      const ctx = canvas.getContext('2d');

      // White background (helps OCR)
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Render PDF page to canvas
      await page.render({ canvasContext: ctx, viewport }).promise;

      // ── Image preprocessing for better OCR accuracy ──
      // Increase contrast and sharpen (accounting docs have thin fonts)
      try {
        const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imgData.data;
        // High-contrast binarization: make light pixels white, dark pixels black
        for (let px = 0; px < data.length; px += 4) {
          const gray = data[px] * 0.299 + data[px+1] * 0.587 + data[px+2] * 0.114;
          const val = gray < 140 ? 0 : 255; // threshold at 140 (aggressive for thin fonts)
          data[px] = data[px+1] = data[px+2] = val;
        }
        ctx.putImageData(imgData, 0, 0);
      } catch (imgErr) {
        console.warn('[OCR] Image preprocessing failed:', imgErr.message);
      }

      console.log(`[OCR] Running OCR on page ${pageNum} (${canvas.width}x${canvas.height})...`);

      // Run OCR
      const result = await worker.recognize(canvas);
      const data = result.data || result;

      if (data && data.lines && data.lines.length > 0) {
        console.log(`[OCR] Page ${pageNum}: found ${data.lines.length} lines`);
        data.lines.forEach(line => {
          const text = (line.text || '').trim();
          if (text && text.length > 2) {
            const parts = (line.words || []).map(w => ({
              x: Math.round((w.bbox ? w.bbox.x0 : 0) / SCALE),
              text: w.text || '',
            }));
            allLines.push({ text, parts, y: Math.round((line.bbox ? line.bbox.y0 : 0) / SCALE), page: pageNum });
          }
        });
      } else {
        console.warn(`[OCR] Page ${pageNum}: no lines recognized`);
      }

      // Cleanup canvas memory
      canvas.width = 0;
      canvas.height = 0;
    } catch (pageErr) {
      console.warn(`[OCR] Page ${pageNum} failed:`, pageErr.message || pageErr);
    }
  }

  try { await worker.terminate(); } catch (e) { /* ignore */ }
  console.log(`[OCR] Complete: ${allLines.length} total lines extracted`);
  return allLines;
};

// ═════ PDF Text Extraction ═════
export const extractPdfStructured = async (file) => {
  let pdfjs;
  try {
    pdfjs = await getPdfjs();
  } catch (e) {
    throw new Error('PDF.js library failed to load. Check your internet connection and refresh the page. Error: ' + e.message);
  }
  if (!pdfjs) throw new Error('PDF.js library not available. Please refresh the page and try again.');

  let abOriginal;
  try {
    abOriginal = await file.arrayBuffer();
  } catch (e) {
    throw new Error('Could not read the file. It may be corrupted or too large. Error: ' + e.message);
  }

  // PDF.js transfers (detaches) the ArrayBuffer, so keep a copy for OCR fallback
  let ab = abOriginal.slice(0); // copy for first getDocument call
  const abForOcr = abOriginal.slice(0); // separate copy reserved for OCR

  let pdf;
  try {
    pdf = await pdfjs.getDocument({ data: ab }).promise;
  } catch (e) {
    throw new Error('Could not parse PDF. The file may be encrypted, corrupted, or password-protected. Error: ' + e.message);
  }

  if (!pdf || !pdf.numPages) throw new Error('PDF has no pages. The file may be empty or corrupted.');

  const all = [];
  let totalTextItems = 0;

  for (let i = 1; i <= pdf.numPages; i++) {
    try {
      const pg = await pdf.getPage(i);
      // Try standard extraction first
      let ct = await pg.getTextContent();
      let items = (ct && ct.items) ? ct.items : [];

      // If standard extraction yields nothing, try with different options
      if (items.length === 0) {
        try {
          ct = await pg.getTextContent({ normalizeWhitespace: true, disableCombineTextItems: false });
          items = (ct && ct.items) ? ct.items : [];
        } catch (e2) { /* ignore fallback failure */ }
      }

      // If still nothing, try with combineTextItems disabled
      if (items.length === 0) {
        try {
          ct = await pg.getTextContent({ normalizeWhitespace: false, disableCombineTextItems: true });
          items = (ct && ct.items) ? ct.items : [];
        } catch (e3) { /* ignore fallback failure */ }
      }

      totalTextItems += items.length;
      const lm = {};

      items.forEach(it => {
        // Some PDFs use different transform structures
        if (!it.transform || it.transform.length < 6) return;
        const y = Math.round(it.transform[5] / 3) * 3;
        const x = Math.round(it.transform[4]);
        const text = it.str || '';
        if (!lm[y]) lm[y] = [];
        lm[y].push({ x, text });
      });

      Object.keys(lm).map(Number).sort((a, b) => b - a).forEach(y => {
        const lineItems = lm[y].sort((a, b) => a.x - b.x);
        const lt = lineItems.map(it => it.text).join(' ').trim();
        if (lt) all.push({ text: lt, parts: lineItems, y, page: i });
      });
    } catch (pageErr) {
      console.warn(`Failed to extract page ${i}:`, pageErr);
      // Continue with other pages
    }
  }

  // ── Last resort: try getDocument with different flags if nothing extracted ──
  if (all.length === 0 && totalTextItems === 0) {
    console.warn('[ReconAI] Standard extraction failed. Trying with useSystemFonts...');
    try {
      const pdf2 = await pdfjs.getDocument({ data: abOriginal.slice(0), useSystemFonts: true, isEvalSupported: false }).promise;
      for (let i = 1; i <= pdf2.numPages; i++) {
        try {
          const pg = await pdf2.getPage(i);
          const ct = await pg.getTextContent();
          if (ct && ct.items) {
            totalTextItems += ct.items.length;
            const lm = {};
            ct.items.forEach(it => {
              if (!it.transform || it.transform.length < 6) return;
              const y = Math.round(it.transform[5] / 3) * 3;
              const x = Math.round(it.transform[4]);
              if (!lm[y]) lm[y] = [];
              lm[y].push({ x, text: it.str || '' });
            });
            Object.keys(lm).map(Number).sort((a, b) => b - a).forEach(y => {
              const lineItems = lm[y].sort((a, b) => a.x - b.x);
              const lt = lineItems.map(it => it.text).join(' ').trim();
              if (lt) all.push({ text: lt, parts: lineItems, y, page: i });
            });
          }
        } catch (e) { /* continue */ }
      }
    } catch (e) {
      console.warn('[ReconAI] useSystemFonts fallback also failed:', e.message);
    }
  }

  // ── OCR Fallback: Render PDF pages to canvas and use Tesseract.js ──
  let ocrAttempted = false, ocrError = '';
  if (all.length === 0) {
    console.warn(`[ReconAI] Text extraction failed (${totalTextItems} items). Attempting OCR via page rendering...`);
    ocrAttempted = true;
    try {
      const ocrLines = await ocrPdfPages(pdfjs, abForOcr, pdf.numPages);
      if (ocrLines.length > 0) {
        console.log(`[ReconAI OCR] Extracted ${ocrLines.length} lines via OCR`);
        return { lines: ocrLines, numPages: pdf.numPages };
      }
      ocrError = 'OCR completed but no text recognized';
    } catch (ocrErr) {
      ocrError = ocrErr.message;
      console.warn('[ReconAI] OCR fallback failed:', ocrErr.message);
    }
  }

  if (all.length === 0) {
    const solutions = [
      'Open the PDF in your ERP/Tally → Export as Excel/CSV',
      'Open in Chrome → Print → "Save as PDF" (creates new text-based PDF)',
      'Copy-paste data into Excel and upload as .xlsx',
      'Use an OCR tool (Adobe Acrobat, Google Drive) to convert to searchable PDF first',
    ];
    const reason = totalTextItems === 0
      ? 'The PDF uses drawn text (vector outlines) instead of actual text characters — no PDF library can read this.'
      : `PDF has ${totalTextItems} text fragments but no readable lines could be formed.`;
    const ocrNote = ocrAttempted ? `\nOCR was attempted but failed: ${ocrError}` : '';
    throw new Error(`No text extracted from ${pdf.numPages} page(s). ${reason}${ocrNote}\n\nSolutions:\n${solutions.map((s, i) => `${i+1}. ${s}`).join('\n')}`);
  }

  return { lines: all, numPages: pdf.numPages };
};

// ═════ Main Orchestrator ═════
export const parsePdfFile = async (file) => {
  const { lines, numPages } = await extractPdfStructured(file);
  console.log(`[ReconAI PDF] Extracted ${lines.length} lines from ${numPages} pages of "${file.name}"`);
  if (lines.length > 0) console.log('[ReconAI PDF] First 3 lines:', lines.slice(0, 3).map(l => l.text));
  if (!lines.length) throw new Error('No text extracted. May be scanned. Try CSV/Excel.');
  const lt = lines.map(l => l.text || l);
  const at = lt.join('\n').toUpperCase();

  let fmt = 'unknown';
  if (/SUBLEDGER\s*RPT|WIBSERP/i.test(at)) fmt = 'wibserp';
  else if (/SUNDRY\s*(CREDITORS|DEBTORS)/i.test(at)) fmt = 'tally-subledger';
  else if (/CREDITORS\s*FOR\s*EXPENSE/i.test(at)) fmt = 'creditor-expense';
  else if (/VCH\s*TYPE.*PARTICULARS/i.test(at)) fmt = 'tally-party';
  else fmt = 'generic';

  // Count To/By lines — also match "ToOpening", "ByHDFC" (no space between prefix and word)
  const tbc = lt.filter(l => /^\s*(\d{1,2}-[A-Za-z]{3}-\d{2,4}\s+)?(To|By)\s*/i.test(l.trim()) && /^(\d{1,2}-[A-Za-z]{3}-\d{2,4}\s+)?(To|By)/i.test(l.trim())).length;
  if (tbc >= 3) fmt = 'tally-classic';

  // Count Cr/Dr prefix lines (Tally party ledger format B)
  const crDrCount = lt.filter(l => /^\s*(\d{1,2}-[A-Za-z]{3}-\d{2,4}\s+)?(Cr|Dr)\s+/i.test(l.trim())).length;

  // Check for Tally Party Ledger format (Vch Type + Vch No columns)
  const hasVchType = /VCH\s*TYPE/i.test(at) && /VCH\s*NO/i.test(at);

  const strats = [
    { name: 'Tally Party Ledger', fn: () => parseTallyPartyLedger(lines), bonus: hasVchType ? 60 + (crDrCount >= 3 ? 30 : 0) : (crDrCount >= 3 ? 20 : 0) },
    { name: 'Tally Classic', fn: () => parseTallyClassic(lines), bonus: fmt === 'tally-classic' ? 50 : tbc >= 2 ? 20 : 0 },
    { name: 'Subledger Block', fn: () => parseSubledgerBlock(lines), bonus: /wibserp/i.test(fmt) ? 80 : /tally|creditor/i.test(fmt) ? 30 : 0 },
    { name: 'Structured Table', fn: () => parseStructuredTable(lines), bonus: /wibserp/i.test(fmt) ? -50 : /party/i.test(fmt) ? 25 : 0 },
    { name: 'Universal', fn: () => parseGenericLedger(lines), bonus: /wibserp/i.test(fmt) ? -50 : /generic/i.test(fmt) ? 20 : 5 },
  ];

  const res = [];
  for (const s of strats) {
    try {
      const r = s.fn(); if (r.transactions.length > 0) {
        let sc = r.transactions.length + s.bonus;
        const dr = r.transactions.reduce((a, t) => a + t.debit, 0), cr = r.transactions.reduce((a, t) => a + t.credit, 0);
        if (dr > 0 && cr > 0) sc += 15;
        sc += new Set(r.transactions.map(t => t.date)).size * 2;
        res.push({ ...r, strategy: s.name, score: sc });
      }
    } catch (e) { console.warn('Strategy failed:', s.name, e); }
  }

  if (!res.length) throw new Error(`Could not parse ${lines.length} lines. Try CSV/Excel.`);
  res.sort((a, b) => b.score - a.score);
  const best = res[0];
  return {
    transactions: best.transactions, metadata: best.metadata,
    detectedFormat: fmt, strategyUsed: best.strategy,
    lineCount: lines.length, pageCount: numPages,
    alternativeResults: res.slice(1).map(r => ({ strategy: r.strategy, count: r.transactions.length, score: r.score }))
  };
};

// ── CSV/Excel Text Parser ───────────────
export const parseDelimitedText = (text) => {
  const lines = text.trim().split('\n');
  const delim = text.includes('\t') ? '\t' : ',';
  const rows = lines.map(l => l.split(delim).map(p => p.trim())).filter(r => r.some(Boolean));
  if (rows.length < 2) return [];
  return rows.slice(1).map(row => {
    if (row.length < 4) return null;
    const db = parseAmount(row[3]), cr = parseAmount(row[4] || '0');
    if (!db && !cr) return null;
    const dn = row[1] || '';
    return makeTx(row[0], dn, row[2] || '', db, cr, { accountType: '' });
  }).filter(Boolean);
};
